import { useRef, useState } from 'react';

/**
 * What a student has done with one block. VIEW state, deliberately not on
 * LabContainer: where a box sits and whether it is folded is a property of the
 * bench being looked at, not of the chemistry in the vessel. Keeping it here
 * means the code that computes a reaction never has to know a layout rule —
 * an earlier cut put it on the container and had to teach four separate
 * result-handling paths about folding boxes.
 */
export interface BlockState {
  /** Collapsed to its icon. */
  minimised?: boolean;
  /** Moved from where the bench put it, in scene units. Both or neither. */
  dx?: number;
  dy?: number;
}

/**
 * One of the two blocks beside a vessel: what is in it, and what it did.
 *
 * Both are the same object as far as the bench is concerned — a box the student
 * can move out of the way or fold down to its icon — so both are this. What
 * differs is the icon and what is inside.
 *
 * The chrome lives in the HTML rather than as SVG siblings so it hugs the box
 * that is actually drawn: the card shrink-wraps its content, so handles placed
 * at the slot's corners would float in space beside it.
 *
 * Dragging converts client pixels to scene units by dividing by the scene's
 * own scale. The block's stored offset is therefore in the same units as every
 * other position on the bench, and survives a zoom change or a rack promotion
 * without being recomputed.
 */
export function BenchBlock({
    kind, icon, title, state, zoom, onChange, className, style, children,
}: {
    kind: 'info' | 'reaction';
    /** Shown in the corner, and alone when the block is folded down. A drawn
     *  mark rather than a character: it has to read at 14px and at 20px. */
    icon: React.ReactNode;
    /** The control's accessible name — "Contents" or "Reaction". */
    title: string;
    state: BlockState | undefined;
    /** The scene's current scale, so a drag in screen pixels lands in scene units. */
    zoom: number;
    onChange: (next: BlockState) => void;
    className?: string;
    style?: React.CSSProperties;
    children: React.ReactNode;
}): React.JSX.Element {
    const drag = useRef<{ x: number; y: number; dx: number; dy: number } | null>(null);
    const [dragging, setDragging] = useState(false);

    const startDrag = (e: React.PointerEvent) => {
        e.stopPropagation();
        e.preventDefault();
        // The element the handler is on, not the one pressed: on a phone the
        // whole box is the handle, and a press lands on whatever text is under
        // the finger — capture there and the drag ends when that span re-renders.
        (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
        drag.current = { x: e.clientX, y: e.clientY, dx: state?.dx ?? 0, dy: state?.dy ?? 0 };
        setDragging(true);
    };
    const onMove = (e: React.PointerEvent) => {
        if (!drag.current) return;
        e.stopPropagation();
        const scale = zoom || 1;
        onChange({
            ...state,
            dx: drag.current.dx + (e.clientX - drag.current.x) / scale,
            dy: drag.current.dy + (e.clientY - drag.current.y) / scale,
        });
    };
    const endDrag = (e: React.PointerEvent) => {
        if (!drag.current) return;
        e.stopPropagation();
        (e.currentTarget as Element).releasePointerCapture?.(e.pointerId);
        drag.current = null;
        setDragging(false);
    };

    const handleProps = {
        className: 'chemlab-block-handle',
        onPointerDown: startDrag,
        onPointerMove: onMove,
        onPointerUp: endDrag,
        onPointerCancel: endDrag,
        'aria-hidden': true as const,
    };

    if (state?.minimised) {
        return (
            <button
                type="button"
                className={`chemlab-block-icon chemlab-block-icon-${kind}`}
                title={`Show ${title.toLowerCase()}`}
                aria-label={`Show ${title.toLowerCase()}`}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); onChange({ ...state, minimised: false }); }}
            >
                {icon}
            </button>
        );
    }

    return (
        /* On a phone the whole box is what you take hold of — the corner
           handles are 12px and only appear on hover, which a finger has no way
           to do. The stylesheet turns this on (pointer-events) only there; on
           a desktop the box stays click-through to the glass and the handles
           are the grip. Its buttons stop their own presses, so folding it and
           opening the menu are still taps. */
        <div
            className={`chemlab-block ${className ?? ''}`}
            style={style}
            data-dragging={dragging ? 'true' : undefined}
            onPointerDown={startDrag}
            onPointerMove={onMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
        >
            {/* Top left, inside the box — the one corner no content starts in. */}
            <button
                type="button"
                className="chemlab-block-minimise"
                title={`Minimise ${title.toLowerCase()}`}
                aria-label={`Minimise ${title.toLowerCase()}`}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); onChange({ ...state, minimised: true }); }}
            >
                {icon}
            </button>
            {/* Two handles, on the corners the block's own marks do not take:
                minimise is top-left and the menu — where a block has one — is
                bottom-left, so the grabbable pair is the right-hand edge. Two
                of them rather than one, so whichever is clear of the glassware
                on a crowded bench can be taken hold of. */}
            <span {...handleProps} data-corner="tr" />
            <span {...handleProps} data-corner="br" />
            {children}
        </div>
    );
}
